import { useState, useRef, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { Upload, Loader2, CheckCircle2, AlertTriangle, XCircle, FileText, Sparkles, Trash2, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABELS = (k) => ({
  licence_number: "licence number", licence_expiry: "licence expiry", home_address: "address",
  cpc_expiry: "CPC expiry", tacho_card_expiry: "tacho card expiry", mot_due: "MOT due", service_due: "service due",
}[k] || (k || "").replace(/_/g, " "));

const humanize = (k) => (k || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// status: queued | processing | filed | attention | unreadable | error
const STATUS_META = {
  queued: { color: "text-slate-500", bg: "bg-slate-100", icon: Loader2, label: "Waiting" },
  processing: { color: "text-blue-700", bg: "bg-blue-100", icon: Loader2, label: "Reading…" },
  filed: { color: "text-emerald-700", bg: "bg-emerald-100", icon: CheckCircle2, label: "Filed" },
  attention: { color: "text-amber-700", bg: "bg-amber-100", icon: AlertTriangle, label: "Needs attention" },
  unreadable: { color: "text-amber-700", bg: "bg-amber-100", icon: AlertTriangle, label: "Couldn't read" },
  error: { color: "text-red-700", bg: "bg-red-100", icon: XCircle, label: "Failed" },
  undone: { color: "text-slate-500", bg: "bg-slate-100", icon: RotateCcw, label: "Undone" },
};

export function ScanInboxPanel() {
  const [items, setItems] = useState([]); // {id, name, status, docLabel, matchLabel, updated[], fields, note}
  const [running, setRunning] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const idRef = useRef(0);
  const queueRef = useRef([]);
  const runningRef = useRef(false);
  const [log, setLog] = useState([]);
  const [undoingLog, setUndoingLog] = useState(null);

  const loadLog = useCallback(async () => {
    try { const res = await api.get("/ai/scan-filings"); setLog(res.data || []); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadLog(); }, [loadLog]);

  const processFile = async (file, itemId) => {
    const patch = (p) => setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...p } : it)));
    patch({ status: "processing" });
    try {
      // 1. Smart extract (detect + read) — send the raw file once
      const exFd = new FormData();
      exFd.append("file", file);
      const ex = await api.post("/ai/smart-extract", exFd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = ex.data || {};

      if (!d.detected_type) {
        patch({ status: "unreadable", note: d.error || "The AI couldn't recognise this document type." });
        return;
      }
      const cleanFields = {};
      Object.entries(d.fields || {}).forEach(([k, v]) => { if (v != null && v !== "") cleanFields[k] = v; });

      const m = d.match;
      if (m && m.id) {
        // 2. Store the file so it can be attached, then auto-file to the matched record
        const upFd = new FormData();
        upFd.append("file", file);
        const up = await api.post("/upload", upFd, { headers: { "Content-Type": "multipart/form-data" } });
        const uploaded = { file_id: up.data.file_id, filename: up.data.filename, content_type: up.data.content_type };
        const res = await api.post("/ai/file-to-record", {
          kind: m.kind, record_id: m.id, updates: m.updates || {},
          attachment: uploaded, attach_field: m.attach_field, doc_label: d.doc_label,
        });
        const updated = (res.data?.updated || []).map(FIELD_LABELS);
        patch({ status: "filed", docLabel: d.doc_label, matchLabel: m.label, matchKind: m.kind, updated, fields: cleanFields,
          undo: { log_id: res.data?.log_id } });
        loadLog();
      } else {
        patch({ status: "attention", docLabel: d.doc_label, fields: cleanFields,
          note: d.detected_type === "insurance" || d.detected_type === "olicence"
            ? "No single vehicle/driver to auto-file — add it manually from the relevant record or Office Documents."
            : "No confident match to a driver/vehicle. Check the details, then file it manually to the right record." });
      }
    } catch (e) {
      patch({ status: "error", note: e?.response?.data?.detail || "Something went wrong processing this file." });
    }
  };

  const drainQueue = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setRunning(true);
    while (queueRef.current.length) {
      const it = queueRef.current.shift();
      await processFile(it.file, it.id);
    }
    runningRef.current = false;
    setRunning(false);
  }, []);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const newItems = files.map((f) => ({ id: ++idRef.current, name: f.name, file: f, status: "queued", updated: [], fields: {} }));
    setItems((prev) => [...prev, ...newItems]);
    queueRef.current.push(...newItems);
    drainQueue();
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    addFiles(e.dataTransfer?.files);
  };

  const clearDone = () => setItems((prev) => prev.filter((it) => it.status === "queued" || it.status === "processing"));
  const clearAll = () => { if (!running) setItems([]); };

  const undoFile = async (item) => {
    if (!item?.undo?.log_id || item.undoing) return;
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, undoing: true } : it)));
    try {
      await api.post("/ai/undo-file-to-record", { log_id: item.undo.log_id });
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, status: "undone", undo: null, undoing: false, updated: [] } : it)));
      toast.success(`Undone — ${item.matchLabel} restored`);
      loadLog();
    } catch (e) {
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, undoing: false } : it)));
      toast.error(e?.response?.data?.detail || "Could not undo");
    }
  };

  const undoLogEntry = async (entry) => {
    if (!entry?.id || undoingLog) return;
    setUndoingLog(entry.id);
    try {
      await api.post("/ai/undo-file-to-record", { log_id: entry.id });
      toast.success(`Undone — ${entry.record_label} restored`);
      // reflect in any in-session row referencing this log
      setItems((prev) => prev.map((it) => (it.undo?.log_id === entry.id ? { ...it, status: "undone", undo: null, updated: [] } : it)));
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not undo");
    } finally { setUndoingLog(null); }
  };

  const counts = items.reduce((acc, it) => { acc[it.status] = (acc[it.status] || 0) + 1; return acc; }, {});
  const total = items.length;
  const doneCount = (counts.filed || 0) + (counts.attention || 0) + (counts.unreadable || 0) + (counts.error || 0);

  return (
    <div data-testid="scan-inbox-panel" className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2">
          <Sparkles size={22} className="text-blue-600" /> Scan Inbox
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          Drop a pile of documents (licences, CPC & tacho cards, MOT/CVRT, PMI, insurance, operator licence).
          HaulCheck reads each one, works out who or what it belongs to, and files it onto the matching driver or vehicle — updating expiry dates automatically.
        </p>
      </div>

      <label
        data-testid="scan-inbox-drop"
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-12 px-6 text-center cursor-pointer transition-colors ${
          dragActive ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-slate-50"
        }`}
      >
        <Upload size={30} className={dragActive ? "text-blue-600" : "text-slate-400"} />
        <span className="text-sm font-semibold text-slate-700">
          {dragActive ? "Drop to scan & auto-file" : "Drop documents here, or click to choose"}
        </span>
        <span className="text-xs text-slate-400">Images or PDFs · you can select many at once</span>
        <input
          ref={inputRef}
          data-testid="scan-inbox-input"
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
      </label>

      {total > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold" data-testid="scan-inbox-summary">
              <span className="rounded-full bg-slate-100 text-slate-600 px-2.5 py-1">{doneCount}/{total} processed</span>
              {counts.filed > 0 && <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1">{counts.filed} filed</span>}
              {counts.attention > 0 && <span className="rounded-full bg-amber-100 text-amber-700 px-2.5 py-1">{counts.attention} need attention</span>}
              {counts.unreadable > 0 && <span className="rounded-full bg-amber-100 text-amber-700 px-2.5 py-1">{counts.unreadable} unreadable</span>}
              {counts.error > 0 && <span className="rounded-full bg-red-100 text-red-700 px-2.5 py-1">{counts.error} failed</span>}
              {running && <span className="flex items-center gap-1 text-blue-600"><Loader2 size={13} className="animate-spin" /> processing…</span>}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" data-testid="scan-inbox-clear-done" onClick={clearDone} disabled={running || doneCount === 0}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors">
                <RotateCcw size={13} /> Clear done
              </button>
              <button type="button" data-testid="scan-inbox-clear-all" onClick={clearAll} disabled={running}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors">
                <Trash2 size={13} /> Clear all
              </button>
            </div>
          </div>

          <div className="space-y-2" data-testid="scan-inbox-list">
            {items.map((it) => {
              const meta = STATUS_META[it.status] || STATUS_META.queued;
              const Icon = meta.icon;
              const spin = it.status === "queued" || it.status === "processing";
              return (
                <div key={it.id} data-testid={`scan-item-${it.id}`} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
                  <div className="mt-0.5 shrink-0"><FileText size={20} className="text-slate-400" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-medium text-slate-800" title={it.name}>{it.name}</p>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.bg} ${meta.color}`} data-testid={`scan-item-status-${it.id}`}>
                        <Icon size={12} className={spin ? "animate-spin" : ""} /> {meta.label}
                      </span>
                    </div>
                    {it.docLabel && <p className="mt-0.5 text-xs text-slate-500">Detected: <span className="font-medium text-slate-700">{it.docLabel}</span></p>}
                    {it.status === "filed" && (
                      <p className="mt-1 text-xs text-emerald-700">
                        Filed to <b>{it.matchLabel}</b>{it.matchKind === "vehicle" ? " (vehicle)" : " (driver)"}
                        {it.updated?.length > 0 && <> — updated {it.updated.join(", ")}</>}
                      </p>
                    )}
                    {it.status === "filed" && it.undo && (
                      <button type="button" data-testid={`scan-item-undo-${it.id}`} onClick={() => undoFile(it)} disabled={it.undoing}
                        className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                        {it.undoing ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} {it.undoing ? "Undoing…" : "Undo this filing"}
                      </button>
                    )}
                    {it.status === "undone" && (
                      <p className="mt-1 text-xs text-slate-500">Filing reversed — <b>{it.matchLabel}</b> restored to its previous details.</p>
                    )}
                    {(it.status === "attention" || it.status === "unreadable" || it.status === "error") && it.note && (
                      <p className="mt-1 text-xs text-amber-800">{it.note}</p>
                    )}
                    {it.status === "attention" && it.fields && Object.keys(it.fields).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                        {Object.entries(it.fields).map(([k, v]) => (
                          <span key={k} className="text-[11px] text-slate-500">{humanize(k)}: <span className="font-medium text-slate-700">{String(v)}</span></span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {log.length > 0 && (
        <div className="pt-2" data-testid="scan-inbox-log">
          <h3 className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
            <History size={15} className="text-slate-500" /> Recently filed
          </h3>
          <p className="text-xs text-slate-400 mt-0.5 mb-2">Filings stay here even after you clear the list above, so you can still undo a mistake.</p>
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
            {log.map((e) => (
              <div key={e.id} data-testid={`scan-log-${e.id}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-800">
                    <b>{e.record_label || "record"}</b>
                    {e.doc_label && <span className="text-slate-500"> · {e.doc_label}</span>}
                    {e.updated?.length > 0 && <span className="text-slate-400 text-xs"> · updated {e.updated.map(FIELD_LABELS).join(", ")}</span>}
                  </p>
                  <p className="text-[11px] text-slate-400">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</p>
                </div>
                {e.undone ? (
                  <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 text-[11px] font-semibold">
                    <RotateCcw size={11} /> Undone
                  </span>
                ) : (
                  <button type="button" data-testid={`scan-log-undo-${e.id}`} onClick={() => undoLogEntry(e)} disabled={undoingLog === e.id}
                    className="shrink-0 inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                    {undoingLog === e.id ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} {undoingLog === e.id ? "Undoing…" : "Undo"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ScanInbox() {
  return (
    <div data-testid="scan-inbox-page" className="max-w-3xl">
      <ScanInboxPanel />
    </div>
  );
}
